<script lang="ts" setup>
import Avatar from '@/components/Avatar.vue'
import PopOverNav from '@/components/navigation/PopOverNav.vue'
import SkrButton from '@/components/button/SkrButton.vue'
import CreditsBadge from '@/components/payment/CreditsBadge.vue'

const { t } = useI18n()

const route = useRoute()

const systemStore = useSystemStore()
const businessStore = useBusinessStore()
const { mdAndUp } = useDisplay()
const { navigationItems } = useSubNav('profile')
const { isNewFreePlan, isScalePlan, isStarterPlan } = useUserPlanInfo()

const platform = computed(() => systemStore.platform || 'CH')
const supportContact = computed(() => businessStore.settings.supportContact)

const emailContactLink = computed(() => {
  return supportContact.value?.email
    ? `mailto:${supportContact.value.email}`
    : platform.value === 'EU'
      ? t('global.navigation.contact.link_form_de')
      : t('global.navigation.contact.link')
})

const openHelpLink = () => {
  // Do not open link on smaller screens to allow touchscreens to hover
  if (mdAndUp.value) {
    window.open(t('global.navigation.helpcenter.link'))
  }
}

const goToProfile = () => {
  if (mdAndUp.value || route.name !== 'profile') {
    navigateTo({ name: 'profile' })
  }
}

const { openFeaturebase } = useFeaturebaseSso()
const { $featurebase } = useNuxtApp()

const { isSupportContactFlag } = useNavigationFlags()
</script>

<template>
  <div class="nav__app-bar py-0 d-inline-flex align-center">
    <credits-badge v-if="isStarterPlan" is-link />
    <div
      v-if="!isScalePlan"
      class="nav__btn py-0 px-2 d-flex align-center flex-row justify-center cursor-pointer"
      :ripple="false"
      color="primary"
      exact
      data-cy="feedback_button"
      @click="openFeaturebase()"
    >
      <v-icon>custom:feedback</v-icon>
      <span class="nav__label pl-3">{{ t('feedback.featurebase.label') }}</span>
    </div>

    <button
      v-if="!isScalePlan"
      type="button"
      class="nav__btn py-0 px-2 d-flex align-center flex-row justify-center cursor-pointer"
      data-cy="changelog_button"
      data-featurebase-changelog
    >
      <span v-if="$featurebase.unreadChangelogCount > 0" class="nav__changelog-badge">
        {{ $featurebase.unreadChangelogCount }}
      </span>
      <v-icon v-else>custom:changelog</v-icon>
      <span class="nav__label pl-3">{{ t('changelog.title') }}</span>
    </button>

    <v-menu
      location="bottom right"
      content-class="nav__help-overview"
      open-delay="150"
      open-on-hover
      :close-on-content-click="true"
    >
      <template #activator="{ props }">
        <div
          v-bind="props"
          class="nav__btn py-0 px-2 d-flex align-center flex-row justify-center"
          :ripple="false"
          color="primary"
          exact
          data-cy="help_icon"
          @click="openHelpLink"
        >
          <v-icon>custom:c_question</v-icon>
          <span class="nav__label pl-3">{{ t('global.navigation.help_center') }}</span>
        </div>
      </template>
      <v-list nav class="pa-1">
        <v-list-item
          :href="t('global.navigation.helpcenter.link')"
          target="_blank"
          rel="noopener"
          data-cy="help_center_link"
        >
          <template #prepend>
            <v-icon>custom:help_center</v-icon>
          </template>
          <v-list-item-title>
            {{ t('global.navigation.helpcenter.label') }}
          </v-list-item-title>
        </v-list-item>
        <v-list-item
          :href="t('global.navigation.tutorials.link')"
          target="_blank"
          rel="noopener"
          data-cy="video_tutorials_link"
        >
          <template #prepend>
            <v-icon>custom:video_tutorials</v-icon>
          </template>
          <v-list-item-title>
            {{ t('global.navigation.tutorials.label') }}
          </v-list-item-title>
        </v-list-item>
        <v-list-item
          v-if="isSupportContactFlag"
          :href="emailContactLink"
          target="_blank"
          rel="noopener"
          data-cy="support_form"
        >
          <template #prepend>
            <v-icon>custom:contact_email</v-icon>
          </template>
          <v-list-item-title>
            {{
              supportContact.email
                ? t('global.navigation.internal_support_contact.mail.label')
                : t('global.navigation.contact.label')
            }}
          </v-list-item-title>
        </v-list-item>
        <v-list-item
          v-if="supportContact?.phone"
          :href="`tel:${supportContact.phone}`"
          target="_blank"
          rel="noopener"
          data-cy="support_phone_number"
        >
          <template #prepend>
            <v-icon>custom:support_hotline</v-icon>
          </template>
          <v-list-item-title>
            {{ t('global.navigation.call.label') }}
          </v-list-item-title>
          <v-list-item-subtitle>
            {{ supportContact.phone ?? t('global.skribble_support_phone_number') }}
          </v-list-item-subtitle>
        </v-list-item>
      </v-list>
    </v-menu>

    <v-menu location="bottom right" open-delay="150" open-on-hover :close-on-content-click="true">
      <template #activator="{ props }">
        <div
          :ripple="false"
          color="primary"
          class="nav__btn px-0 pl-0 pr-1 d-flex justify-center align-center"
          data-cy="profileButton"
          v-bind="props"
          @click="goToProfile"
        >
          <avatar custom-class="nav__avatar" />
          <v-icon>custom:arrow_down</v-icon>
        </div>
      </template>
      <pop-over-nav :items="navigationItems" />
    </v-menu>

    <skr-button v-if="isNewFreePlan" size="lg" to="/upgrade">{{ t('global.upgrade') }}</skr-button>
  </div>
</template>

<style lang="scss" scoped>
.nav {
  &__app-bar {
    gap: $bw * 2;
  }

  &__avatar {
    font-size: $h4-font-size;
  }

  &.v-list--nav {
    padding: 0;
  }

  &.v-list-item {
    padding-left: 0;
    padding-right: 0;

    &--nav:not(:only-child) {
      margin-bottom: 0;
    }
  }

  &__label {
    margin-top: 2 * $bw;
    padding-left: $bw;
    padding-right: $bw;
    text-align: center;
    white-space: normal;
    line-height: 1.2;
    padding-bottom: 2 * $bw;
    font-size: $h4-font-size;
    font-weight: bold;
  }
  &__help-overview {
    .v-list-item {
      &__content {
        width: min-content;
        font-size: 0.75rem;
        color: $c-grey-dark;

        .v-list-item-title {
          font-weight: 700;
          font-size: $h4-font-size;
        }

        &__sub-title,
        .v-list-item__action > span > svg {
          font-size: 0.75rem;
          color: $c-grey-dark;
        }
      }
    }
  }
  &__btn {
    position: relative;
    background: none;
    border: none;
    font-family: inherit;
    color: inherit;

    &:before {
      content: '';
      position: absolute;
      top: -4px;
      left: -4px;
      right: -4px;
      bottom: -4px;
      background-color: transparent;
      border-radius: 2rem;
      transition: background-color 0.2s ease-in-out;
      z-index: -1;
    }

    &:hover,
    &[aria-expanded='true'] {
      &:before {
        background-color: $c-grey-bg-6;
      }
    }
  }

  &__changelog-badge {
    min-width: 24px;
    height: 24px;
    padding: 0 6px;
    font-size: $h4-font-size;
    font-weight: 700;
    line-height: 24px;
    text-align: center;
    color: $c-primary;
    background-color: $c-primary-bg;
    border-radius: 6px;
  }
}
</style>

<!-- Global styles for Featurebase dropdown positioning -->
<style lang="scss">
.fb-widget-container-changelog {
  margin-top: 12px !important;
  border-radius: 12px !important;
  overflow: hidden !important;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15) !important;
}
</style>
